class AnnouncementBar extends HTMLElement {
  constructor() {
    super();

    this.currentIndex = 0;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.announcementBar = this.querySelector("#wt-announcement__container");
    this.announcementInterval = null;
    // Kept in sync with the transition duration set in connectedCallback —
    // the slide swap is delayed by this long so it happens while the
    // outgoing slide is fully faded out (opacity 0), reading as a crossfade
    // rather than a horizontal slide.
    this.fadeDurationMs = 300;

    this.changeAnnouncement = this.changeAnnouncement.bind(this);
    this.previousAnnouncement = this.previousAnnouncement.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.hideForOneDay = this.hideForOneDay.bind(this);
    this.restartInterval = this.restartInterval.bind(this);
    this.pauseInterval = this.pauseInterval.bind(this);
    this.isMobileView = this.isMobileView.bind(this);
    this.getSlideIntervalMs = this.getSlideIntervalMs.bind(this);
    this.getSlideCount = this.getSlideCount.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  getSlideCount() {
    if (!this.announcementBar) return 0;
    if (
      this.announcementBar.classList.contains(
        "wt-announcement__container--marquee",
      )
    ) {
      return 0;
    }

    const countAttr = this.announcementBar.dataset.announcementsCount;
    if (countAttr !== undefined && countAttr !== "") {
      const parsed = parseInt(countAttr, 10);
      if (!Number.isNaN(parsed)) return parsed;
    }

    return this.announcementBar.children.length;
  }

  // Reads the --time custom property (set inline from section.settings.
  // animation_speed, in seconds — see sections/announcement-bar.liquid) so
  // the same setting drives both the marquee scroll speed and this
  // non-marquee slide auto-advance interval. Falls back to 5000ms if the
  // property is missing or unparsable (e.g. single-block sections, where
  // --time is never set).
  getSlideIntervalMs() {
    const fallbackMs = 5000;
    if (!this.announcementBar) return fallbackMs;

    const raw = getComputedStyle(this.announcementBar)
      .getPropertyValue("--time")
      .trim();
    const seconds = parseFloat(raw);

    return Number.isFinite(seconds) && seconds > 0
      ? seconds * 1000
      : fallbackMs;
  }

  pauseInterval() {
    clearInterval(this.announcementInterval);
  }

  connectedCallback() {
    const hasMultipleMessages = this.getSlideCount() > 1;

    if (hasMultipleMessages) {
      this.announcementInterval = setInterval(
        this.changeAnnouncement,
        this.getSlideIntervalMs(),
      );

      if (this.announcementBar) {
        this.announcementBar.addEventListener(
          "touchstart",
          this.handleTouchStart,
          { passive: true },
        );
        this.announcementBar.addEventListener(
          "touchmove",
          this.handleTouchMove,
          {
            passive: true,
          },
        );
        this.announcementBar.addEventListener("touchend", this.handleTouchEnd, {
          passive: true,
        });
        this.announcementBar.addEventListener(
          "touchstart",
          this.pauseInterval,
          { passive: true },
        );
        this.announcementBar.addEventListener(
          "touchend",
          this.restartInterval,
          {
            passive: true,
          },
        );
        this.announcementBar.style.transition = `opacity ${this.fadeDurationMs}ms ease-in-out`;
      }
    } else if (this.announcementBar) {
      this.announcementBar.style.transition = "none";
      this.announcementBar.style.opacity = "1";
    }

    this.closeButton = this.querySelector(".wt-announcement__close");
    if (this.closeButton)
      this.closeButton.addEventListener("click", this.hideForOneDay);

    window.addEventListener("resize", this.handleResize);
  }

  handleResize() {
    // Reset the translateX to its initial state so a resize (orientation
    // change, breakpoint crossing) doesn't leave the carousel mid-slide.
    // Also force opacity back to 1 in case resize lands mid-fade.
    if (this.announcementBar) {
      this.announcementBar.style.transform = "translateX(0)";
      this.announcementBar.style.opacity = "1";
      this.currentIndex = 0;
    }
  }

  isMobileView() {
    return window.innerWidth < 900;
  }

  restartInterval() {
    clearInterval(this.announcementInterval);
    if (this.getSlideCount() > 1) {
      this.announcementInterval = setInterval(
        this.changeAnnouncement,
        this.getSlideIntervalMs(),
      );
    }
  }

  hideForOneDay() {
    const oneDayLater = new Date().getTime() + 24 * 60 * 60 * 1000;
    localStorage.setItem("wt-announcement-hidden", oneDayLater);
    this.style.display = "none";
  }

  // Slide width is measured in real pixels rather than assumed to be 100vw:
  // on desktop each message is sized via CSS (width:100cqw against
  // .wt-announcement__viewport, see critical.css) to the narrower visible
  // slot next to the left/right link snippets, not the full viewport — so a
  // literal 100vw here would overshoot and misalign the translateX offset.
  getSlideWidth() {
    return this.announcementBar?.children[0]?.getBoundingClientRect().width || 0;
  }

  // Only one slide is ever visible at a time — .wt-announcement__viewport
  // clips to exactly one slide's width, and translateX just moves which
  // slide sits inside that window (see critical.css). So a crossfade
  // doesn't need per-slide opacity or a layout change: fading the whole
  // track out, jumping its position while invisible, then fading back in
  // reads identically to crossfading the individual slide.
  fadeToPosition(newIndex) {
    if (!this.announcementBar || this.getSlideCount() <= 1) return;

    this.currentIndex = newIndex;
    this.announcementBar.style.opacity = "0";

    setTimeout(() => {
      const newPosition = -(this.currentIndex * this.getSlideWidth());
      this.announcementBar.style.transform = `translateX(${newPosition}px)`;
      this.announcementBar.style.opacity = "1";
    }, this.fadeDurationMs);
  }

  changeAnnouncement() {
    const totalSlides = this.getSlideCount();
    if (totalSlides <= 1) return;

    this.fadeToPosition((this.currentIndex + 1) % totalSlides);
  }

  previousAnnouncement() {
    const totalSlides = this.getSlideCount();
    if (totalSlides <= 1) return;

    this.fadeToPosition((this.currentIndex - 1 + totalSlides) % totalSlides);
  }

  handleTouchStart(e) {
    if (this.isMobileView()) {
      this.touchStartX = e.touches[0].clientX;
    }
  }

  handleTouchMove(e) {
    if (this.isMobileView()) {
      this.touchEndX = e.touches[0].clientX;
    }
  }

  handleTouchEnd() {
    if (this.getSlideCount() <= 1) return;

    const swipeThreshold = 30;

    if (Math.abs(this.touchEndX - this.touchStartX) > swipeThreshold) {
      if (this.touchEndX < this.touchStartX) {
        this.changeAnnouncement();
      } else {
        this.previousAnnouncement();
      }
    }
  }

  disconnectedCallback() {
    clearInterval(this.announcementInterval);
    if (this.announcementBar) {
      this.announcementBar.removeEventListener(
        "touchstart",
        this.handleTouchStart,
      );
      this.announcementBar.removeEventListener("touchmove", this.handleTouchMove);
      this.announcementBar.removeEventListener("touchend", this.handleTouchEnd);
      this.announcementBar.removeEventListener(
        "touchstart",
        this.pauseInterval,
      );
      this.announcementBar.removeEventListener(
        "touchend",
        this.restartInterval,
      );
    }
    window.removeEventListener("resize", this.handleResize);
  }
}

customElements.define("announcement-bar", AnnouncementBar);
